import { isFieldErrorResponse, type ShipmentCreate } from 'api';
import { useNotification } from 'features';
import { Form, type SubmitHandler, useForm } from 'form';
import { useTranslation } from 'i18n';
import type { ChangeEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, Button, Grid, Page, Typography } from 'ui';

import { BookingConfirmationDoc } from '../components/BookingConfirmationDoc';
import { ClearOverridesSection } from '../components/ClearOverridesSection';
import { LoadsSection } from '../components/LoadsSection';
import { ShipmentCreateFieldsSection } from '../components/ShipmentCreateFieldsSection';
import { useCreateShipment } from '../hooks/useCreateShipment';

export const ShipmentCreatePage = () => {
  const { t } = useTranslation('orders');
  const { salesOrderSlug } = useParams();
  const { open } = useNotification();
  const navigate = useNavigate();

  if (!salesOrderSlug) {
    throw new Error('Sales Order slug is not set');
  }
  const form = useForm<ShipmentCreate>();
  const {
    formState: { isDirty },
    getValues,
    setError,
    setValue,
  } = form;

  const { createShipment, isPending, error } = useCreateShipment();

  const onSubmit: SubmitHandler<ShipmentCreate> = async (data) => {
    try {
      // @ts-expect-error form is missing an accurate schema
      const bookingConfirmation = getValues('booking_confirmation');
      const load_ids = (data.load_ids as unknown as { id: number; name: string }[]).map(({ id }) => id) as number[];
      // @ts-expect-error form is missing an accurate schema
      const response = await createShipment(bookingConfirmation, {
        sales_order_slug: salesOrderSlug,
        ...data,
        load_ids,
      });

      open({ message: t('shipmentCreatePage.notifications.shipmentCreatedSuccessfully'), type: 'success' });

      // Redirect to the update page for the newly created shipment
      if (response?.name) {
        navigate(`/ground-control/v2/shipment/${response.name}`);
      }
    } catch {
      if (isFieldErrorResponse(error) && error.response?.data) {
        Object.entries(error.response.data).forEach(([field, message]) => {
          // @ts-expect-error form is missing an accurate schema
          setError(field, { message });
        });
      }
    }
  };
  //
  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // @ts-expect-error form is missing an accurate schema
      setValue('booking_confirmation', file, { shouldDirty: true });
    }
  };
  return (
    <Page>
      <Form {...form} onSubmit={onSubmit}>
        {error ? (
          <Box
            sx={{
              paddingY: 2,
            }}
          >
            <Alert severity="error">{error.message}</Alert>
          </Box>
        ) : null}
        <Box
          sx={{
            mb: 3,
          }}
        >
          <Typography variant="h5" fontWeight="bold">
            {t('shipmentCreatePage.title', { values: { salesOrderSlug } })}
          </Typography>
        </Box>
        <BookingConfirmationDoc handleFileChange={handleFileChange} />
        <LoadsSection hasShipment={false} salesOrderSlug={salesOrderSlug} />
        <ShipmentCreateFieldsSection />
        <ClearOverridesSection />
        <Grid size={12}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              paddingY: 3,
            }}
          >
            <Button type="submit" size="large" loading={isPending} disabled={!isDirty}>
              {t('shipmentCreatePage.actions.createShipment')}
            </Button>
          </Box>
        </Grid>
      </Form>
    </Page>
  );
};
